import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useProductEditor } from '../product-editor-context'
import { useImageUpload, getYouTubeThumbnail } from '@/hooks/use-image-upload'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Upload,
  X,
  Image as ImageIcon,
  GripVertical,
  Loader2,
  PlayCircle,
  AlertCircle,
  Link as LinkIcon,
  Star,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { MediaItem } from '@/types/database'

interface SortableMediaItemProps {
  item: MediaItem
  index: number
  isPrimary: boolean
  onRemove: () => void
  onToggleReview?: () => void
}

function SortableMediaItem({ item, index, isPrimary, onRemove, onToggleReview }: SortableMediaItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${item.type}-${item.url}-${index}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Get display thumbnail for videos - ALWAYS derive from URL for YouTube to prevent stale thumbnails
  const thumbnailUrl = item.type === 'video'
    ? getYouTubeThumbnail(item.url) || item.thumbnail_url || undefined
    : item.url

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative aspect-[4/3] rounded-xl border-2 overflow-hidden bg-zinc-100 min-h-[140px]',
        isDragging ? 'opacity-50 border-primary' : 'border-zinc-200',
        isPrimary && 'ring-2 ring-primary ring-offset-2'
      )}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={`${item.type === 'video' ? 'Video' : 'Image'} ${index + 1}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Hide broken image and show fallback
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : null}

      {/* Fallback when no thumbnail or image fails to load */}
      {!thumbnailUrl && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-200 gap-2">
          {item.type === 'video' ? (
            <>
              <PlayCircle className="size-10 text-zinc-400" />
              <span className="text-xs text-zinc-500">Video</span>
            </>
          ) : (
            <>
              <ImageIcon className="size-10 text-zinc-400" />
              <span className="text-xs text-zinc-500">Image</span>
            </>
          )}
        </div>
      )}

      {/* Video indicator overlay - only show when not hovering */}
      {item.type === 'video' && thumbnailUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none group-hover:opacity-0 transition-opacity">
          <PlayCircle className="size-12 text-white drop-shadow-lg" />
        </div>
      )}

      {/* Badges container - always visible */}
      <div className="absolute top-2 left-2 right-2 flex items-start justify-between pointer-events-none z-10">
        <div className="flex flex-col gap-1">
          {/* Primary badge */}
          {isPrimary && (
            <div className="bg-primary text-white text-xs font-medium px-2 py-1 rounded shadow">
              Primary
            </div>
          )}

          {/* Video type badge */}
          {item.type === 'video' && !isPrimary && !item.is_review && (
            <div className="bg-black/70 text-white text-xs font-medium px-2 py-1 rounded shadow">
              Video
            </div>
          )}

          {/* Review video badge */}
          {item.type === 'video' && item.is_review && (
            <div className="bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded shadow flex items-center gap-1">
              <Star className="size-3 fill-current" />
              Review
            </div>
          )}
        </div>
      </div>

      {/* Hover controls - larger and more visible */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="size-12 cursor-grab active:cursor-grabbing shadow-lg"
            {...attributes}
            {...listeners}
            title="Drag to reorder"
          >
            <GripVertical className="size-6" />
          </Button>
          {item.type === 'video' && onToggleReview && (
            <Button
              type="button"
              size="icon"
              variant={item.is_review ? 'default' : 'secondary'}
              className={cn(
                'size-12 shadow-lg',
                item.is_review && 'bg-amber-500 hover:bg-amber-600'
              )}
              onClick={onToggleReview}
              title={item.is_review ? 'Remove as review video' : 'Set as review video'}
            >
              <Star className={cn('size-6', item.is_review && 'fill-current')} />
            </Button>
          )}
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="size-12 shadow-lg"
            onClick={onRemove}
            title="Remove"
          >
            <X className="size-6" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export function MediaSection() {
  const { form, media, addMedia, addMediaFromUrl, removeMedia, reorderMedia, toggleReviewVideo } = useProductEditor()
  const { uploadImages, uploadingImages, removeUploadingImage } = useImageUpload()
  const [urlInput, setUrlInput] = useState('')

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle file drops (images only)
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const results = await uploadImages(acceptedFiles)
    results.forEach(result => {
      if (result.status === 'complete' && result.url) {
        addMedia({ type: 'image', url: result.url })
      }
    })
  }, [uploadImages, addMedia])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif'],
    },
    multiple: true,
  })

  // Handle URL input - auto-detects video URLs
  const handleAddUrl = () => {
    if (urlInput.trim()) {
      addMediaFromUrl(urlInput.trim())
      setUrlInput('')
    }
  }

  // Handle drag end for reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = media.findIndex((item, i) => `${item.type}-${item.url}-${i}` === active.id)
      const newIndex = media.findIndex((item, i) => `${item.type}-${item.url}-${i}` === over.id)
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderMedia(oldIndex, newIndex)
      }
    }
  }

  // Create sortable IDs
  const sortableIds = media.map((item, i) => `${item.type}-${item.url}-${i}`)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Media</CardTitle>
        <CardDescription>
          Drag to reorder. First item (image or video) becomes the product thumbnail in listings.
          For videos, mark one as "Review" using the star button.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Dropzone */}
        <div className="space-y-4">
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-zinc-300 hover:border-zinc-400'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto size-10 text-zinc-400 mb-4" />
            <p className="text-sm text-zinc-600">
              {isDragActive
                ? 'Drop images here...'
                : 'Drag & drop images here, or click to select'}
            </p>
            <p className="text-xs text-zinc-400 mt-1">
              JPEG, PNG, WebP, GIF up to 10MB
            </p>
          </div>

          {/* URL input - supports both images and video URLs */}
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="Paste image or YouTube/Vimeo URL..."
                className="pl-9"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddUrl())}
              />
            </div>
            <Button type="button" variant="outline" onClick={handleAddUrl} disabled={!urlInput.trim()} className="w-full sm:w-auto">
              Add Media
            </Button>
          </div>

          {/* Uploading indicator */}
          {uploadingImages.filter(img => img.status === 'uploading').length > 0 && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="size-4 animate-spin" />
              Uploading {uploadingImages.filter(img => img.status === 'uploading').length} image(s)...
            </div>
          )}

          {/* Upload errors */}
          {uploadingImages.filter(img => img.status === 'error').map(img => (
            <div key={img.id} className="flex items-center gap-2 text-sm text-red-500">
              <AlertCircle className="size-4" />
              {img.fileName}: {img.error}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeUploadingImage(img.id)}
              >
                Dismiss
              </Button>
            </div>
          ))}

          {/* Media Gallery */}
          {media.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {media.map((item, index) => (
                    <SortableMediaItem
                      key={`${item.type}-${item.url}-${index}`}
                      item={item}
                      index={index}
                      isPrimary={index === 0}
                      onRemove={() => removeMedia(index)}
                      onToggleReview={item.type === 'video' ? () => toggleReviewVideo(index) : undefined}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* No media placeholder */}
          {media.length === 0 && (
            <div className="text-center py-8 text-sm text-zinc-400 border rounded-lg bg-zinc-50">
              <ImageIcon className="mx-auto size-8 mb-2" />
              No media yet. Upload images or paste URLs above.
            </div>
          )}

          {/* Info text */}
          {media.length > 0 && (
            <p className="text-xs text-zinc-500">
              <strong>Tip:</strong> Hover over items to see controls. Drag to reorder, star to mark as review video, X to delete.
            </p>
          )}
        </div>

        {/* Affiliate link */}
        <FormField
          control={form.control}
          name="affiliate_link"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Affiliate Link</FormLabel>
              <FormControl>
                <Input placeholder="https://..." {...field} />
              </FormControl>
              <FormDescription>External purchase link (optional)</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}
